/**
 * CDD Records Store (Zustand)
 * Manages all CDD records in memory regardless of UI visibility
 * Handles real-time updates from WebSocket
 * Persists data even when components unmount
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { CDDStatus } from '../types/cdd-records.types';
import { normalizeCDDStatus } from '../utils/cddStatus';

export interface CDDRecord {
  id: string;
  reference: string;
  customer: string;
  email: string;
  abn: string;
  type: string;
  status: CDDStatus;
  riskLevel: 'low' | 'medium' | 'high';
  createdDate: string;
  documents: any[];
}

const normalizeRecordUpdates = (updates: any, existingStatus?: CDDStatus): Partial<CDDRecord> => {
  const { reportStatus, stage, currentStage, currentOnboardingStage, outcome, ...recordUpdates } = updates;
  const statusValues = [
    existingStatus,
    updates.status,
    updates.newStatus,
    reportStatus,
    stage?.status ?? stage,
    currentStage?.status ?? currentStage,
    currentOnboardingStage?.status ?? currentOnboardingStage,
    outcome,
  ];
  if (statusValues.some((value) => value != null && String(value).trim())) {
    recordUpdates.status = normalizeCDDStatus(...statusValues);
  }
  delete recordUpdates.newStatus;
  return recordUpdates;
};

interface CddRecordsState {
  // Data storage
  records: Map<string, CDDRecord>;
  allRecords: CDDRecord[];

  // Metadata
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: string | null;

  // Actions
  setRecords: (records: CDDRecord[]) => void;
  addRecord: (record: CDDRecord) => void;
  updateRecord: (id: string, updates: Partial<CDDRecord>) => void;
  deleteRecord: (id: string) => void;

  // Real-time updates from WebSocket
  handleRecordStatusChanged: (
    id: string,
    status: unknown,
    previousStatus?: string,
    context?: Record<string, unknown>,
  ) => void;
  handleRecordUpdated: (updates: any) => void;
  handleDashboardUpdated: (metrics: any) => void;

  // Selectors & Queries
  getRecordById: (id: string) => CDDRecord | undefined;
  getRecordsByStatus: (status: string) => CDDRecord[];
  getRecordsByRiskLevel: (riskLevel: string) => CDDRecord[];
  getStats: () => {
    total: number;
    completed: number;
    pending: number;
    notStarted: number;
    failed: number;
    highRisk: number;
    inProgress: number;
  };

  // Metadata updates
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTotalCount: (count: number) => void;

  // Session management
  clearRecords: () => void;
}

export const useCddRecordsStore = create<CddRecordsState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    records: new Map(),
    allRecords: [],
    totalCount: 0,
    isLoading: false,
    error: null,
    lastUpdatedAt: null,

    // Set all records (initial load) — replace, don't merge, to avoid infinite loops
    setRecords: (newRecords: CDDRecord[]) => {
      set(() => {
        const recordsMap = new Map<string, CDDRecord>();
        newRecords.forEach((record) => {
          recordsMap.set(record.id, record);
        });

        return {
          records: recordsMap,
          allRecords: newRecords,
          totalCount: newRecords.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Add single record
    addRecord: (record: CDDRecord) => {
      set((state) => {
        const newMap = new Map(state.records);
        newMap.set(record.id, record);
        const newAllRecords = [...state.allRecords, record];


        return {
          records: newMap,
          allRecords: newAllRecords,
          totalCount: newAllRecords.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Update single record (from real-time updates)
    updateRecord: (id: string, updates: Partial<CDDRecord>) => {
      set((state) => {
        const existing = state.records.get(id);
        const normalizedUpdates = normalizeRecordUpdates(updates, existing?.status);

        if (!existing) {
          // If record doesn't exist, create it with the updates
          const newRecord = { ...normalizedUpdates, id } as CDDRecord;
          const newMap = new Map(state.records);
          newMap.set(id, newRecord);
          const newAllRecords = [...state.allRecords, newRecord];

          return {
            records: newMap,
            allRecords: newAllRecords,
            totalCount: newAllRecords.length,
            lastUpdatedAt: new Date().toISOString(),
          };
        }

        const updated = { ...existing, ...normalizedUpdates };
        const newMap = new Map(state.records);
        newMap.set(id, updated);

        const newAllRecords = state.allRecords.map((r) =>
          r.id === id ? updated : r
        );

        return {
          records: newMap,
          allRecords: newAllRecords,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Delete record
    deleteRecord: (id: string) => {
      set((state) => {
        const newMap = new Map(state.records);
        newMap.delete(id);
        const newAllRecords = state.allRecords.filter((r) => r.id !== id);


        return {
          records: newMap,
          allRecords: newAllRecords,
          totalCount: newAllRecords.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Handle real-time status change
    handleRecordStatusChanged: (id, status, _previousStatus, context = {}) => {
      const existing = get().allRecords.find((record) => record.id === id || record.reference === id);
      get().updateRecord(existing?.id ?? id, { ...context, status } as Partial<CDDRecord>);
    },

    // Handle real-time record update
    handleRecordUpdated: (updates: any) => {
      const rawId = updates?.id ?? updates?.caseId ?? updates?.referenceNumber;
      if (!rawId) return;
      const incomingId = String(rawId);
      const existing = get().allRecords.find(
        (record) => record.id === incomingId || record.reference === incomingId,
      );
      const id = existing?.id ?? incomingId;
      get().updateRecord(id, { ...updates, id });
    },

    // Handle dashboard metrics (ensure all records exist)
    handleDashboardUpdated: (metrics: any) => {
      // This is informational - actual record updates come via CDDRecordUpdated
    },

    // Get record by ID
    getRecordById: (id: string) => {
      return get().records.get(id);
    },

    // Get records by status
    getRecordsByStatus: (status: string) => {
      return get().allRecords.filter((r) => r.status === status);
    },

    // Get records by risk level
    getRecordsByRiskLevel: (riskLevel: string) => {
      return get().allRecords.filter((r) => r.riskLevel === riskLevel);
    },

    // Get statistics
    getStats: () => {
      const records = get().allRecords;
      return {
        total: records.length,
        completed: records.filter((r) => r.status === 'completed').length,
        pending: records.filter((r) => r.status === 'pending').length,
        notStarted: records.filter((r) => r.status === 'not-started').length,
        failed: records.filter((r) => r.status === 'failed').length,
        inProgress: records.filter((r) => r.status === 'in-progress').length,
        highRisk: records.filter((r) => r.riskLevel === 'high').length,
      };
    },

    // Metadata setters
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    setTotalCount: (count: number) => {
      set({ totalCount: count });
    },

    clearRecords: () => {
      set({
        records: new Map(),
        allRecords: [],
        totalCount: 0,
        isLoading: false,
        error: null,
        lastUpdatedAt: null,
      });
    },
  }))
);

// Debug logging middleware
if (process.env.NODE_ENV === 'development') {
  useCddRecordsStore.subscribe(
    (state) => state.allRecords,
    (records) => {
    }
  );
}

export default useCddRecordsStore;
